import { Autocomplete, Divider, Paper, TextField, Drawer, Box } from "@mui/material"
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { usePageLoader } from "../contexts/page_content_context";
import { processError } from "../utils/base_error";
import { usePageStartBootstrap } from "../hooks/use_page_start_bootstrap";
import { zodResolver } from "@hookform/resolvers/zod";
import { DropDownProdutoServicoDto } from "../domain/models/dropdown_produto_servico";
import { defaultProdutoServicoForm, ProdutoServicoForm, ProdutoServicoSchema } from "../../features/compras/presentation/schemas/ProdutosServicos_lista/ProdutoServico";
import { FormRadioGroupInput } from "./masked_inputs/react_forms/form_radio_group_input";
import DynamicIcon from "./dynamic_icon";
import DefaultFormInput from "./default_form_input";
import PrimaryButton from "./primary_button";
import { useDropDownLoader } from "../hooks/use_dropdown_loader";

/**
 * DropDownProdutoServico
 */

type Props<F extends {}> = {
  name: keyof F,
  label?: string,
  newButtonLabel?: string,
  required?: boolean,
  readOnly?: boolean,
  placeholder?: string,
  reactHookForm: UseFormReturn<F>,
  tipo?: string,
  incluirCodigoDescricao?: boolean,
};

export const DropDownProdutoServico = <F extends {},>({
  name,
  label,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,
  tipo,
  incluirCodigoDescricao,
}: Props<F>) => {

  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();
  const [produtoServicoDropDown, setProdutoServicoDropDown] = useState<DropDownProdutoServicoDto[]>([]);

  const {
    compras: {
      createProdutoServicoUseCase,
    }
  } = useDependencyInjection();

  const { loadProdutoServicosDropDown } = useDropDownLoader();

  const hookForm = useForm<ProdutoServicoForm>({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    defaultValues: defaultProdutoServicoForm,
    resolver: zodResolver(ProdutoServicoSchema),
  });

  usePageStartBootstrap(async () => {
    await loadProdutoServicosDropDown(dt =>
      setProdutoServicoDropDown(dt.map(x => ({
        id: x.idProdutoServico, title: x.descricao, ativo: x.ativo, unidade: x.unidade
      }))), { incluirCodigoDescricao: true });
  });

  const hanldleSalvarProdutoServico = useCallback(async (form: ProdutoServicoForm) => {
    try {
      setSaving(true);

      const idProdutoServico = await createProdutoServicoUseCase.createProdutoServico(form);
      reactHookForm.setValue(name as any, idProdutoServico as any);
      pushInfoNotification("SUCESSO", "Produto/Serviço criado com sucesso!!", { isAutoFinish: true })

    } catch (e: unknown) {
      pushErrorNotification("Erro", ["Falha ao salvar Produto/Serviço"], { isAutoFinish: true })
    } finally {
      setSaving(false);

      await loadProdutoServicosDropDown(dt =>
        setProdutoServicoDropDown(dt.map(x => ({
          id: x.idProdutoServico, title: x.descricao, ativo: x.ativo, unidade: x.unidade
        }))), { incluirCodigoDescricao: true });

      setOpenModal(false);
      hookForm.reset();
    }
  }, [
    name,
    reactHookForm,
    hookForm,
    createProdutoServicoUseCase,
    loadProdutoServicosDropDown,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (novoTipo: number) => {
    try {
      hookForm.setValue("tipo", novoTipo);
    } catch (e: unknown) {
      pushErrorNotification("Erro", "Falha ao mudar o Tipo!")
    }
  }, [
    hookForm,
    pushErrorNotification
  ]);

  //  BOTÃO 
  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          {children}

          <Divider />
          <div className="bg-white rounded-b-[4px]">
            <div
              className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
              onMouseDown={(e) => {
                e.preventDefault();
                setOpenModal(true);
              }}
              role="button"
              tabIndex={0}>
              <DynamicIcon
                nameIcon="Add"
                size={20}
                className="text-primary-green"
              />
              <span>{newButtonLabel ?? "Novo"}</span>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel]
  );

  return (
    <>
      {/* DROPDOWN */}
      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === 'string') {
              helperText = fieldError.message;
            }
          }

          return (
            <Autocomplete
              ListboxProps={{ style: { maxHeight: 220 } }}
              fullWidth={true}
              size="small"
              disablePortal={false}
              options={produtoServicoDropDown}
              getOptionKey={opt => opt.id}
              getOptionLabel={(opt) => opt.title}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              slots={{
                paper: CustomPaper,
              }}
              sx={{ minHeight: 45 }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={produtoServicoDropDown.find(x => x.id === field.value) ?? null}
              onChange={(_, newValue) => field.onChange(newValue ? newValue.id : undefined)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={label}
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />

      {/* MODAL DE CADASTRO */}

      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1301 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-6">Adicionar Produto/Serviço</h2>
          <div className="flex flex-col items-start w-full gap-6 px-1">
            <div className="w-full flex flex-row items-center justify-start gap-4 p-4 rounded-md border border-gray-300">
              <label className="font-medium text-gray-700 whitespace-nowrap">
                Tipo?
              </label>

              <div className="flex items-center [&_.MuiFormGroup-row]:flex-nowrap">
                <FormRadioGroupInput
                  name="tipo"
                  label=""
                  arrangement="horizontal"
                  boldOptionsLabel={false}
                  readOnly={saving}
                  onValueChange={(e) => {
                    const valor = isNaN(Number(e)) ? e : Number(e);
                    handleStatusChange(valor as number);
                  }}
                  options={[
                    { id: 1, label: "Produto" },
                    { id: 0, label: "Serviço" },
                  ]}
                  reactHookForm={hookForm}
                />
              </div>
            </div>

            <div className="w-full">
              <DefaultFormInput
                name='descricao'
                label='Produto/Serviço'
                multiline={true}
                minRows={5}
                required={true}
                reactHookForm={hookForm}
              />
            </div>

            <div className="w-full">
              <DefaultFormInput
                name='unidade'
                label='Unidade'
                required={false}
                reactHookForm={hookForm}
              />
            </div>

          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >

          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">
                Salvando...
              </PrimaryButton>
            ) : (
              <PrimaryButton
                buttonColor="primary-green"
                onClick={hookForm.handleSubmit(hanldleSalvarProdutoServico)}
              >
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>

    </>
  );
};
